import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { useRouter } from 'next/router'
import { blinkStyles } from 'productTemplate/components/ProductPicker/blinkStyles'
import { getUnderRetailLabel } from 'productTemplate/utils/under-retail-utils'
import { SortOption } from 'search/enums/search'
import { SVGIcon } from 'shared/components/Icons'
import Image from 'shared/components/Image'
import { LinkCTA } from 'shared/components/Links'
import { usePageContext } from 'shared/contexts/PageContextProvider'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { SearchQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import { colors, fonts, media } from 'shared/lib'
import styled, { css } from 'styled-components'
import DesktopGridName from './DesktopGridName'
import MobileGridName from './MobileGridName'

const TP = 'shared.components.ProductGrid.GridItem'

type GridItemProps = {
  brand: string
  className?: string
  currency: string
  dataTrackingEvent?: (...args: any) => void
  hidePrice?: boolean
  imageUrl: string
  index: number
  name: string
  price?: string
  productUrl: string
  isPLP: boolean
  discountPercentage?: number
}

const GridItem = ({
  brand,
  className,
  dataTrackingEvent,
  hidePrice,
  imageUrl,
  index,
  name,
  price,
  productUrl,
  isPLP,
  discountPercentage,
}: GridItemProps) => {
  const pageContext = usePageContext()
  const { country } = useShoppingRegionContext()
  const router = useRouter()
  const isUnderRetailPLPCopyEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_UNDER_RETAIL_PLP_COPY)
  const urlParams = router.query
  const isSearch = 'query' in urlParams

  const displayUnderRetailCopy =
    isUnderRetailPLPCopyEnabled &&
    (isSearch ||
      pageContext.value === SpecificPageTrackingEvents.FilteredGridPage ||
      pageContext.value === SpecificPageTrackingEvents.CollectionPage) //Only display the copy on search result page, collection page and PLP

  const underRetailLabel = getUnderRetailLabel('variant2', discountPercentage || 0, country)

  const handlePdpAnalytics = () => {
    const filterParams = [
      'brand',
      'model',
      'color',
      'condition',
      'size_men',
      'size_women',
      'size_youth',
      'price_cents_usd_max',
      'price_cents_usd_min',
    ]

    const isFiltered = filterParams.filter((filter) => filter in urlParams).length > 0

    const eventContext = (() => {
      if (isSearch) return 'search_results'
      if (pageContext.value === SpecificPageTrackingEvents.ProductTemplatePage) return 'related_pdp'
      if (pageContext.value === SpecificPageTrackingEvents.FilteredGridPage) return 'plp'
      if (pageContext.value === SpecificPageTrackingEvents.CollectionPage) return 'collection'
      return 'discovery_feed'
    })()

    const sortBy = (urlParams.sortBy as SortOption) || SortOption.Popular

    sendTrackingEvent('PDP_TAP', {
      page: window?.location?.href,
      product_template_slug: productUrl,
      is_filtered: isFiltered,
      sort_by: sortBy,
      event_context: eventContext,
      search_string: urlParams.query?.query?.query || urlParams.query || '',
      service_name: 'consumer_search',
      position: index + 1,
    })
  }

  const handleDataTrackingEvent = () => {
    if (dataTrackingEvent) {
      return dataTrackingEvent(index + 1, productUrl)
    }
    return handlePdpAnalytics()
  }

  return (
    <LinkCTA
      color="inherit"
      dataTrackingEvent={handleDataTrackingEvent}
      href={`/${productUrl}`}
      qaAttr={SearchQA.ProductItemsUrl}
    >
      <Wrapper className={className} data-qa={SearchQA.ProductItems}>
        <div
          style={{
            display: 'block',
            marginLeft: '20px',
            marginTop: '16px',
            height: '8px',
            zIndex: 99,
          }}
        >
          {displayUnderRetailCopy && underRetailLabel && (
            <div>
              <UnderRetailLabel>{underRetailLabel}</UnderRetailLabel>
              <IconWrapper>
                <SVGIcon name="underRetailArrow" size={6} margin="0 0 1px" />
              </IconWrapper>
            </div>
          )}
        </div>
        <GridImageContainer isPLP={isPLP}>
          <Image
            alt={name}
            src={imageUrl}
            data-testid="gridItemImage"
            data-qa={SearchQA.ProductItemThumbnail}
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
            width={750}
            height={195}
          />
        </GridImageContainer>

        <GridInfo isPLP={isPLP}>
          <InfoWrapper>
            <GridBrand data-qa={SearchQA.ProductItemBrandName} data-testid="gridItemBrand">
              {brand}
            </GridBrand>
            <MobileGridName name={name} isPLP={isPLP} />
            <DesktopGridName name={name} />
          </InfoWrapper>
          {!hidePrice && price && (
            <div style={{ display: 'inline-flex' }}>
              <GridPrice data-qa={SearchQA.ProductItemPrice} data-testid="gridItemPrice">
                {price}
              </GridPrice>
            </div>
          )}
        </GridInfo>
      </Wrapper>
    </LinkCTA>
  )
}

GridItem.displayName = 'GridItem'

const Wrapper = styled.figure`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  margin: 0;
  background-color: ${colors.FC2_WHITE};
`

const GridImageContainer = styled.div<{ isPLP: boolean }>`
  position: relative;
  ${({ isPLP }) =>
    isPLP
      ? css`
          width: calc(100% - 20px);
          margin: 0 10px 16px;
          height: 124px;
        `
      : css`
          width: calc(100% - 10px);
          margin: 0 10px 30px;
          height: 114px;
        `};
  flex-grow: 1;
  object-fit: contain;
  min-height: 100px;
  ${media.large`
    width: calc(100% - 10px);
    margin: 20px 10px 30px;
    min-height: 150px;
    height: 195px;
    margin: 0 10px 24px;
  `}
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`
const GridInfo = styled.figcaption<{ isPLP: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: ${({ isPLP }) => (isPLP ? '0 20px 24px' : '0 20px 35px')};
  ${media.large`
    padding: 0 20px 24px;
  `}
`

const GridBrand = styled.div`
  ${fonts.SUBTITLE_3};
  color: ${colors.FC2_GREY};
  margin-bottom: 5px;
`

const GridPrice = styled.div`
  margin-top: 10px;
  ${fonts.SUBTITLE_1}
`

const IconWrapper = styled.div`
  display: inline-block;
  ${blinkStyles}
`

const UnderRetailLabel = styled.label`
  ${fonts.SUBTITLE_3};
  color: ${colors.FC2_RED};
  text-transform: none;
  font-weight: 400;
  margin-right: 5px;
  line-height: 120%;
`

export default GridItem
